import { supabase } from './supabase';

export interface AnalyticsConfig {
  id: string;
  business_id: string;
  provider: 'custom' | 'ga4' | 'mixpanel';
  is_active: boolean;
  config: Record<string, any>;
  tracking_code?: string;
  created_at: string;
  updated_at: string;
}

export interface AnalyticsEvent {
  id: string;
  business_id: string;
  event_type: string;
  event_data: Record<string, any>;
  page_url?: string;
  referrer?: string;
  user_agent?: string;
  ip_address?: string;
  session_id?: string;
  visitor_id?: string;
  timestamp: string;
}

export interface AnalyticsMetrics {
  page_views: number;
  unique_visitors: number;
  sessions: number;
  form_submissions: number;
  clicks: number;
  top_pages: Array<{ page: string; views: number }>;
  traffic_sources: Array<{ source: string; visits: number }>;
}

export interface AnalyticsData {
  date: string;
  metrics: AnalyticsMetrics;
}

// Analytics Configuration Service
export const analyticsService = {
  // Get analytics configuration for a business
  async getConfig(businessId: string): Promise<AnalyticsConfig | null> {
    const { data, error } = await supabase
      .from('business_analytics_config')
      .select('*')
      .eq('business_id', businessId)
      .single();

    if (error) {
      console.error('Error fetching analytics config:', error);
      return null;
    }

    return data;
  },

  // Create or update analytics configuration
  async saveConfig(config: Partial<AnalyticsConfig>): Promise<AnalyticsConfig | null> {
    const { data, error } = await supabase
      .from('business_analytics_config')
      .upsert(config, { onConflict: 'business_id' })
      .select()
      .single();

    if (error) {
      console.error('Error saving analytics config:', error);
      return null;
    }

    return data;
  },

  // Generate tracking code for a business
  generateTrackingCode(businessId: string): string {
    const apiUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
    
    return `
<script>
(function() {
  const TRACKING_ID = '${businessId}';
  const API_ENDPOINT = '${apiUrl}/functions/v1/analytics-track';
  const ANON_KEY = '${anonKey}';
  
  // Get or create visitor ID
  function getVisitorId() {
    let visitorId = localStorage.getItem('analytics_visitor_id');
    if (!visitorId) {
      visitorId = 'visitor_' + Math.random().toString(36).substr(2, 9);
      localStorage.setItem('analytics_visitor_id', visitorId);
    }
    return visitorId;
  }
  
  // Get or create session ID
  function getSessionId() {
    let sessionId = sessionStorage.getItem('analytics_session_id');
    if (!sessionId) {
      sessionId = 'session_' + Math.random().toString(36).substr(2, 9);
      sessionStorage.setItem('analytics_session_id', sessionId);
    }
    return sessionId;
  }
  
  // Track page view
  function trackPageView() {
    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + ANON_KEY
      },
      body: JSON.stringify({
        business_id: TRACKING_ID,
        event_type: 'page_view',
        page_url: window.location.href,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
        event_data: {
          title: document.title,
          path: window.location.pathname
        }
      })
    }).catch(console.error);
  }
  
  // Track form submissions
  function trackFormSubmission(formData) {
    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + ANON_KEY
      },
      body: JSON.stringify({
        business_id: TRACKING_ID,
        event_type: 'form_submit',
        page_url: window.location.href,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
        event_data: {
          form_data: formData,
          form_id: formData.form_id || 'unknown'
        }
      })
    }).catch(console.error);
  }
  
  // Track clicks
  function trackClick(element, eventData = {}) {
    fetch(API_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + ANON_KEY
      },
      body: JSON.stringify({
        business_id: TRACKING_ID,
        event_type: 'click',
        page_url: window.location.href,
        referrer: document.referrer,
        user_agent: navigator.userAgent,
        session_id: getSessionId(),
        visitor_id: getVisitorId(),
        event_data: {
          element: element.tagName.toLowerCase(),
          element_id: element.id || '',
          element_class: element.className || '',
          text: element.textContent?.substring(0, 100) || '',
          ...eventData
        }
      })
    }).catch(console.error);
  }
  
  // Initialize tracking
  trackPageView();
  
  // Track form submissions
  document.addEventListener('submit', function(e) {
    if (e.target.tagName === 'FORM') {
      const formData = new FormData(e.target);
      const formDataObj = {};
      for (let [key, value] of formData.entries()) {
        formDataObj[key] = value;
      }
      formDataObj.form_id = e.target.id || e.target.className || 'unknown';
      trackFormSubmission(formDataObj);
    }
  });
  
  // Track important clicks (buttons, links)
  document.addEventListener('click', function(e) {
    const target = e.target;
    if (target.tagName === 'BUTTON' || target.tagName === 'A' || 
        target.closest('button') || target.closest('a')) {
      trackClick(target);
    }
  });
  
  // Track page visibility changes
  document.addEventListener('visibilitychange', function() {
    if (!document.hidden) {
      trackPageView();
    }
  });
  
  // Make tracking functions globally available
  window.analytics = {
    trackPageView,
    trackFormSubmission,
    trackClick,
    trackEvent: function(eventType, eventData) {
      fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + ANON_KEY
        },
        body: JSON.stringify({
          business_id: TRACKING_ID,
          event_type: eventType,
          page_url: window.location.href,
          referrer: document.referrer,
          user_agent: navigator.userAgent,
          session_id: getSessionId(),
          visitor_id: getVisitorId(),
          event_data: eventData
        })
      }).catch(console.error);
    }
  };
})();
</script>`.trim();
  },

  // Get analytics data for a date range
  async getAnalyticsData(
    businessId: string, 
    startDate: string, 
    endDate: string
  ): Promise<AnalyticsData[]> {
    const { data, error } = await supabase
      .from('analytics_data_cache')
      .select('*')
      .eq('business_id', businessId)
      .gte('date', startDate)
      .lte('date', endDate)
      .order('date', { ascending: true });

    if (error) {
      console.error('Error fetching analytics data:', error);
      return [];
    }

    return data || [];
  },

  // Get real-time analytics for today
  async getRealTimeAnalytics(businessId: string): Promise<AnalyticsMetrics | null> {
    const today = new Date().toISOString().split('T')[0];
    
    const { data, error } = await supabase
      .rpc('aggregate_analytics_data', {
        p_business_id: businessId,
        p_date: today
      });

    if (error) {
      console.error('Error fetching real-time analytics:', error);
      return null;
    }

    return data;
  },

  // Get analytics events for debugging
  async getEvents(
    businessId: string, 
    limit: number = 100
  ): Promise<AnalyticsEvent[]> {
    const { data, error } = await supabase
      .from('analytics_events')
      .select('*')
      .eq('business_id', businessId)
      .order('timestamp', { ascending: false })
      .limit(limit);

    if (error) {
      console.error('Error fetching analytics events:', error);
      return [];
    }

    return data || [];
  }
};
